<template>
  <div class="container py-5 my-covoits">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="mb-0">Mes covoiturages</h2>
      <b-button variant="outline-primary" size="sm" @click="fetchCovoits" :disabled="loading">
        <b-icon icon="arrow-clockwise" class="mr-1" />
        Rafraîchir
      </b-button>
    </div>

    <b-alert v-if="error" variant="danger" show class="mb-4">
      {{ error }}
    </b-alert>

    <div v-if="loading" class="text-center py-5">
      <b-spinner label="Chargement..." />
    </div>

    <div v-else>
      <div v-if="!allCovoits.length" class="covoit-empty text-center py-5 text-muted">
        <b-icon icon="car-front" font-scale="2.5" class="mb-3 d-block mx-auto"></b-icon>
        Vous n'avez aucun covoiturage à venir.
      </div>

      <div v-else class="covoit-list">
        <div
          v-for="item in allCovoits"
          :key="item._key"
          :class="['covoit-card', item.scope === 'offered' ? 'covoit-card--driver' : 'covoit-card--passenger']"
          @click="openDetailsModal(item, item.scope)"
        >
          <!-- Miniature carte GPS + date -->
          <div class="covoit-card__left">
            <div
              class="covoit-card__avatar"
              :class="{ 'covoit-card__avatar--clickable': hasLocationGPS(item) }"
              @click.stop="hasLocationGPS(item) && openCovoitLocation(item)"
            >
              <CovoitMapThumb
                v-if="hasLocationGPS(item)"
                :lat="item.covoit.locationGPS.latitude"
                :lng="item.covoit.locationGPS.longitude"
              />
              <div v-else class="driver-avatar">
                {{ driverInitials(item) }}
              </div>
            </div>
            <div class="covoit-card__date-below p-2">
              <b-icon icon="calendar3" class="covoit-date-icon"></b-icon>
              <span class="covoit-date-text">{{ formatDate(item.ouvertureDate) }}</span>
            </div>
          </div>

          <!-- Infos -->
          <div class="covoit-card__body">
            <div class="covoit-card__title">
              {{ item.ouvertureTitle }}
              <span :class="['covoit-badge', item.scope === 'offered' ? 'covoit-badge--driver' : 'covoit-badge--passenger']">
                <b-icon :icon="item.scope === 'offered' ? 'car-front' : 'person-fill'" class="mr-1"></b-icon>
                {{ item.scope === 'offered' ? 'Conducteur' : 'Passager' }}
              </span>
            </div>

            <div class="covoit-card__meta">
              <span class="covoit-meta-item">
                <b-icon icon="geo-alt-fill" class="mr-1 text-primary"></b-icon>{{ item.covoit.location }}
              </span>
              <span class="covoit-meta-item">
                <b-icon icon="clock" class="mr-1"></b-icon>{{ formatDateTime(item.covoit.time) }}
              </span>
              <span v-if="item.covoit.car_model" class="covoit-meta-item text-muted">
                <b-icon icon="car-front" class="mr-1"></b-icon>{{ item.covoit.car_model }}
              </span>
            </div>

            <div class="covoit-card__stats-mobile">
              <span
                class="covoit-stat-mini"
                :class="availableSeats(item) > 0 ? 'covoit-stat-mini--available' : 'covoit-stat-mini--full'"
              >
                <b-icon icon="people-fill" class="mr-1"></b-icon>{{ availableSeats(item) }} place{{ availableSeats(item) !== 1 ? 's' : '' }}
              </span>
              <span class="covoit-stat-mini covoit-stat-mini--neutral">
                <b-icon icon="person-fill" class="mr-1"></b-icon>{{ passengerCount(item) }} passager{{ passengerCount(item) !== 1 ? 's' : '' }}
              </span>
            </div>

            <div v-if="item.scope === 'joined'" class="covoit-card__driver-info">
              <b-icon icon="person-circle" class="mr-1"></b-icon>
              Conducteur : <strong>{{ formatDriver(item.covoit.driver) }}</strong>
            </div>

            <div v-if="item.covoit.info" class="covoit-card__info">
              <b-icon icon="chat-left-text" class="mr-1"></b-icon>{{ item.covoit.info }}
            </div>

            <div class="covoit-card__actions" @click.stop>
              <b-button
                size="sm"
                variant="outline-secondary"
                class="covoit-action-btn"
                @click="openDetailsModal(item, item.scope)"
              >
                <b-icon icon="pencil" class="mr-1"></b-icon>
                {{ item.scope === 'offered' ? 'Gérer' : 'Détails' }}
              </b-button>
              <b-button
                v-if="hasLocationGPS(item)"
                size="sm"
                variant="outline-primary"
                class="covoit-action-btn"
                @click="openCovoitLocation(item)"
              >
                <b-icon icon="map" class="mr-1"></b-icon>Carte
              </b-button>
            </div>
          </div>

          <!-- Stats -->
          <div class="covoit-card__stats">
            <div
              class="covoit-stat"
              :class="availableSeats(item) > 0 ? 'covoit-stat--available' : 'covoit-stat--full'"
            >
              <b-icon icon="people-fill" class="covoit-stat__icon"></b-icon>
              <span class="covoit-stat__value">{{ availableSeats(item) }}</span>
              <span class="covoit-stat__label">place{{ availableSeats(item) !== 1 ? 's' : '' }}</span>
            </div>
            <div class="covoit-stat covoit-stat--neutral">
              <b-icon icon="person-fill" class="covoit-stat__icon"></b-icon>
              <span class="covoit-stat__value">{{ passengerCount(item) }}</span>
              <span class="covoit-stat__label">passager{{ passengerCount(item) !== 1 ? 's' : '' }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal détails (inchangé) -->
    <b-modal
      :visible="detailsModalVisible"
      @update:visible="detailsModalVisible = $event"
      @hidden="detailsModalVisible = false"
      @shown="onDetailsModalShown"
      title="Détails du covoiturage"
      hide-footer
      centered
      size="lg"
    >
      <div v-if="detailsItem" class="carpool-details">
        <div class="details-head mb-4">
          <h5 class="mb-1">{{ detailsItem.ouvertureTitle }}</h5>
          <p class="text-muted mb-0">{{ formatDate(detailsItem.ouvertureDate) }}</p>
        </div>

        <div class="details-card mb-4">
          <div class="details-grid">
            <div>
              <div class="details-label">Départ</div>
              <div class="details-value">{{ detailsItem.covoit.location }}</div>
            </div>
            <div>
              <div class="details-label">Heure</div>
              <div class="details-value">{{ formatDateTime(detailsItem.covoit.time) }}</div>
            </div>
            <div>
              <div class="details-label">Places restantes</div>
              <div class="details-value">{{ detailsItem.covoit.seats - detailsItem.covoit.passenger.length }}/{{ detailsItem.covoit.seats }}</div>
            </div>
            <div>
              <div class="details-label">Véhicule</div>
              <div class="details-value">{{ detailsItem.covoit.car_model || '-' }}</div>
            </div>
          </div>
          <div v-if="detailsItem.covoit.info" class="mt-3 border-top pt-2">
            <div class="details-label">Infos</div>
            <div class="details-value" style="white-space: pre-line;">{{ detailsItem.covoit.info }}</div>
          </div>
          <div v-if="hasLocationGPS(detailsItem)" class="mt-3 text-right">
            <b-button size="sm" variant="outline-info" @click="openCovoitLocation(detailsItem)">
              <b-icon icon="geo-alt" class="mr-1"></b-icon>
              Ouvrir la carte
            </b-button>
          </div>
        </div>

        <template v-if="detailsScope === 'offered'">
          <div class="details-card mb-4">
            <h6 class="mb-3">Modifier les infos</h6>
            <b-form @submit.prevent="submitDriverUpdate">
              <b-form-group label="Point GPS de départ">
                <MapLocationPicker
                  ref="editMapPicker"
                  v-model="detailsForm.locationGPS"
                  @resolved-address="handleEditResolvedAddress"
                />
              </b-form-group>
              <b-form-group label="Adresse de départ">
                <b-form-input :value="detailsForm.location" readonly placeholder="Sélectionnez un point sur la carte..."></b-form-input>
                <small class="text-muted">Remplie automatiquement depuis le point GPS.</small>
              </b-form-group>
              <b-form-row>
                <b-col md="6">
                  <b-form-group label="Heure de départ">
                    <HourMinutePicker v-model="detailsForm.time" />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Places disponibles">
                    <b-form-input v-model.number="detailsForm.seats" type="number" min="1" step="1"></b-form-input>
                  </b-form-group>
                </b-col>
              </b-form-row>
              <b-form-group label="Modèle de voiture">
                <b-form-input v-model.trim="detailsForm.car_model"></b-form-input>
              </b-form-group>
              <b-form-group label="Infos">
                <b-form-textarea
                  v-model.trim="detailsForm.info"
                  placeholder="Ex: Point de rendez-vous précis, code portail, remarques..."
                  rows="2"
                  max-rows="4"
                ></b-form-textarea>
              </b-form-group>
              <div class="text-right">
                <b-button variant="primary" type="submit" :disabled="detailsActionLoading">
                  Enregistrer
                </b-button>
              </div>
            </b-form>
          </div>

          <div class="details-card mb-4">
            <h6 class="mb-2">Inscrits</h6>
            <p v-if="!detailsItem.covoit.passenger.length" class="text-muted mb-0">
              Aucun passager inscrit.
            </p>
            <ul v-else class="mb-0 pl-3">
              <li v-for="passenger in detailsItem.covoit.passenger" :key="passenger._id">
                <div class="contact-name">{{ formatUserName(passenger) }}</div>
                <div class="contact-meta">{{ passenger.email }} · {{ passenger.phoneNumber }}</div>
              </li>
            </ul>
          </div>

          <div class="d-flex justify-content-between align-items-center">
            <b-button variant="outline-danger" :disabled="detailsActionLoading" @click="cancelDriverCovoitFromModal">
              Annuler le covoit
            </b-button>
            <b-button variant="secondary" @click="detailsModalVisible = false">Fermer</b-button>
          </div>
        </template>

        <template v-else>
          <div class="details-card mb-4">
            <h6 class="mb-2">Conducteur</h6>
            <p class="mb-1 contact-name">{{ formatUserName(detailsItem.covoit.driver) }}</p>
            <p class="mb-1 contact-meta">{{ detailsItem.covoit.driver.email }}</p>
            <p class="mb-0 contact-meta">{{ detailsItem.covoit.driver.phoneNumber }}</p>
          </div>

          <div class="d-flex justify-content-between align-items-center">
            <b-button variant="outline-danger" :disabled="detailsActionLoading" @click="leaveJoinedCovoitFromModal">
              Se désinscrire
            </b-button>
            <b-button variant="secondary" @click="detailsModalVisible = false">Fermer</b-button>
          </div>
        </template>
      </div>
    </b-modal>
  </div>
</template>

<script>
import HourMinutePicker from "@/components/HourMinutePicker.vue";
import CovoitMapThumb from "@/components/CovoitMapThumb.vue";
import MapLocationPicker from "@/components/MapLocationPicker.vue";
import OpeningService from "@/services/opening.service";

export default {
  name: "MyCovoits",
  components: {
    HourMinutePicker,
    CovoitMapThumb,
    MapLocationPicker
  },
  data() {
    return {
      offeredCovoits: [],
      joinedCovoits: [],
      loading: true,
      error: null,
      detailsModalVisible: false,
      detailsScope: "",
      detailsItem: null,
      detailsActionLoading: false,
      detailsForm: {
        ouvertureId: "",
        covoitId: "",
        baseDate: "",
        location: "",
        locationGPS: null,
        time: "",
        seats: 1,
        car_model: "",
        info: ""
      }
    };
  },
  computed: {
    allCovoits() {
      const offered = this.offeredCovoits
        .filter((item) => this.isUpcomingCovoit(item.covoit.time))
        .map((item) => ({ ...item, scope: "offered", _key: `offered-${item.ouvertureId}-${item.covoit._id}` }));

      const joined = this.joinedCovoits
        .filter((item) => this.isUpcomingCovoit(item.covoit.time))
        .map((item) => ({ ...item, scope: "joined", _key: `joined-${item.ouvertureId}-${item.covoit._id}` }));

      return [...offered, ...joined].sort(
        (a, b) => new Date(a.covoit.time) - new Date(b.covoit.time)
      );
    }
  },
  mounted() {
    this.fetchCovoits();
  },
  methods: {
    async fetchCovoits() {
      this.loading = true;
      this.error = null;
      try {
        const response = await OpeningService.getMyCovoits();
        if (response.status !== 200) {
          throw new Error("Impossible de recuperer les covoiturages.");
        }
        this.offeredCovoits = response.data.created;
        this.joinedCovoits = response.data.joined;
        this.$nextTick(() => this.openCovoitFromQuery());
      } catch (error) {
        this.error = error && error.message ? error.message : "Une erreur inattendue est survenue.";
      } finally {
        this.loading = false;
      }
    },
    openCovoitFromQuery() {
      const ouvertureId = this.$route.query.ouvertureId;
      if (!ouvertureId) return;
      const item = this.allCovoits.find(i => String(i.ouvertureId) === String(ouvertureId));
      if (item) this.openDetailsModal(item, item.scope);
    },
    isUpcomingCovoit(covoitTime) {
      const today = new Date();
      const covoitDay = new Date(covoitTime);
      const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
      const covoitUtc = Date.UTC(covoitDay.getUTCFullYear(), covoitDay.getUTCMonth(), covoitDay.getUTCDate());
      return covoitUtc >= todayUtc;
    },
    availableSeats(item) {
      const seats = Number(item.covoit.seats);
      const passengers = Array.isArray(item.covoit.passenger) ? item.covoit.passenger.length : 0;
      return Math.max(seats - passengers, 0);
    },
    passengerCount(item) {
      return Array.isArray(item.covoit.passenger) ? item.covoit.passenger.length : 0;
    },
    driverInitials(item) {
      if (item.scope === "offered") return "Moi";
      const d = item.covoit.driver;
      if (!d) return "?";
      return ((d.name || "").charAt(0) + (d.surname || "").charAt(0)).toUpperCase() || "?";
    },
    formatUserName(user) {
      return `${user.name} ${user.surname}`;
    },
    formatDriver(driver) {
      return `${driver.name} ${driver.surname}`;
    },
    formatDate(dateValue) {
      const date = new Date(dateValue);
      if (Number.isNaN(date.getTime())) return "-";
      return date.toLocaleDateString("fr-FR", { day: "numeric", month: "long" });
    },
    formatDateTime(dateValue) {
      const date = new Date(dateValue);
      if (Number.isNaN(date.getTime())) return "-";
      return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    },
    hasLocationGPS(item) {
      if (!item || !item.covoit || !item.covoit.locationGPS || typeof item.covoit.locationGPS !== "object") return false;
      const gps = item.covoit.locationGPS;
      return Number.isFinite(gps.latitude) && Number.isFinite(gps.longitude);
    },
    getCovoitCoordinates(item) {
      if (!this.hasLocationGPS(item)) return null;
      return { latitude: Number(item.covoit.locationGPS.latitude), longitude: Number(item.covoit.locationGPS.longitude) };
    },
    openCovoitLocation(item) {
      const coords = this.getCovoitCoordinates(item);
      if (!coords) return;
      const query = `${coords.latitude},${coords.longitude}`;
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`, "_blank", "noopener,noreferrer");
    },
    formatTimeForInput(dateValue) {
      const date = new Date(dateValue);
      return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
    },
    buildIsoDateTime(baseDate, timeValue) {
      const dateTime = new Date(`${baseDate}T${timeValue}`);
      if (Number.isNaN(dateTime.getTime())) return null;
      return dateTime.toISOString();
    },
    openDetailsModal(item, scope) {
      this.detailsItem = item;
      this.detailsScope = scope;
      if (scope === "offered") {
        const gps = item.covoit.locationGPS;
        this.detailsForm = {
          ouvertureId: String(item.ouvertureId),
          covoitId: String(item.covoit._id),
          baseDate: String(item.ouvertureDate).split("T")[0],
          location: item.covoit.location,
          locationGPS: (gps && Number.isFinite(gps.latitude) && Number.isFinite(gps.longitude))
            ? { latitude: gps.latitude, longitude: gps.longitude }
            : null,
          time: this.formatTimeForInput(item.covoit.time),
          seats: Number(item.covoit.seats),
          car_model: item.covoit.car_model || "",
          info: item.covoit.info || ""
        };
      }
      this.detailsModalVisible = true;
    },
    refreshDetailsItem() {
      if (!this.detailsItem) return;
      const ouvertureId = String(this.detailsItem.ouvertureId);
      const covoitId = String(this.detailsItem.covoit._id);
      const next = this.allCovoits.find(
        (i) => String(i.ouvertureId) === ouvertureId && String(i.covoit._id) === covoitId
      );
      this.detailsItem = next || null;
      if (!next) this.detailsModalVisible = false;
    },
    onDetailsModalShown() {
      if (this.$refs.editMapPicker && typeof this.$refs.editMapPicker.invalidateSize === "function") {
        this.$refs.editMapPicker.invalidateSize();
      }
    },
    hasValidLocationGPS(value) {
      if (!value || typeof value !== "object") return false;
      return Number.isFinite(value.latitude) && Number.isFinite(value.longitude);
    },
    handleEditResolvedAddress(address) {
      if (address) {
        this.detailsForm.location = address;
      } else if (this.hasValidLocationGPS(this.detailsForm.locationGPS)) {
        const { latitude, longitude } = this.detailsForm.locationGPS;
        this.detailsForm.location = `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
      }
    },
    async submitDriverUpdate() {
      if (!this.hasValidLocationGPS(this.detailsForm.locationGPS) || !this.detailsForm.time || Number(this.detailsForm.seats) <= 0) {
        this.$bvToast.toast("Veuillez sélectionner un point GPS et renseigner des informations valides.", { title: "Erreur", variant: "danger", solid: true });
        return;
      }
      let locationValue = this.detailsForm.location;
      if (!locationValue) {
        const { latitude, longitude } = this.detailsForm.locationGPS;
        locationValue = `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
      }
      const time = this.buildIsoDateTime(this.detailsForm.baseDate, this.detailsForm.time);
      if (!time) {
        this.$bvToast.toast("Horaire de départ invalide.", { title: "Erreur", variant: "danger", solid: true });
        return;
      }
      this.detailsActionLoading = true;
      try {
        await OpeningService.updateCovoit(this.detailsForm.ouvertureId, this.detailsForm.covoitId, {
          seats: Number(this.detailsForm.seats),
          location: locationValue,
          locationGPS: this.detailsForm.locationGPS,
          time,
          car_model: this.detailsForm.car_model,
          info: this.detailsForm.info
        });
        await this.fetchCovoits();
        this.refreshDetailsItem();
        this.$bvToast.toast("Covoiturage mis à jour.", { title: "Succès", variant: "success", solid: true });
      } catch (error) {
        this.$bvToast.toast(error.message || "Impossible de mettre à jour le covoiturage.", { title: "Erreur", variant: "danger", solid: true });
      } finally {
        this.detailsActionLoading = false;
      }
    },
    async cancelDriverCovoitFromModal() {
      if (!this.detailsItem) return;
      if (!confirm("Annuler ce covoiturage ?")) return;
      this.detailsActionLoading = true;
      try {
        await OpeningService.deleteCovoit(String(this.detailsItem.ouvertureId), String(this.detailsItem.covoit._id));
        await this.fetchCovoits();
        this.detailsModalVisible = false;
        this.$bvToast.toast("Covoiturage annulé.", { title: "Succès", variant: "success", solid: true });
      } catch (error) {
        this.$bvToast.toast(error.message || "Impossible d'annuler le covoiturage.", { title: "Erreur", variant: "danger", solid: true });
      } finally {
        this.detailsActionLoading = false;
      }
    },
    async leaveJoinedCovoitFromModal() {
      if (!this.detailsItem) return;
      if (!confirm("Se désinscrire de ce covoiturage ?")) return;
      this.detailsActionLoading = true;
      try {
        await OpeningService.leaveCovoit(String(this.detailsItem.ouvertureId), String(this.detailsItem.covoit._id));
        await this.fetchCovoits();
        this.detailsModalVisible = false;
        this.$bvToast.toast("Désinscription effectuée.", { title: "Succès", variant: "success", solid: true });
      } catch (error) {
        this.$bvToast.toast(error.message || "Impossible de se désinscrire.", { title: "Erreur", variant: "danger", solid: true });
      } finally {
        this.detailsActionLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.my-covoits h2 {
  color: #172b4d;
}

.covoit-empty {
  color: #8a9ab0;
}

/* Liste */
.covoit-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

/* Card */
.covoit-card {
  display: flex;
  align-items: stretch;
  gap: 0.85rem;
  background: #fff;
  border: 1px solid #e4e8ef;
  border-radius: 0.6rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.covoit-card:hover {
  border-color: #b8c5d6;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
}

.covoit-card--driver {
  border-left: 3px solid #2563eb;
}

.covoit-card--passenger {
  border-left: 3px solid #28a745;
}

/* Avatar / map thumb */
.covoit-card__left {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.35rem;
  width: 88px;
}

.covoit-card__avatar {
  width: 88px;
  flex: 1;
  min-height: 88px;
}

.covoit-card__date-below {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: #475569;
  text-align: center;
  line-height: 1.2;
  background: #f1f5f9;
  border-radius: 0.35rem;
  padding: 0.2rem 0.35rem;
  width: 100%;
}

.covoit-date-icon {
  flex-shrink: 0;
  font-size: 0.75rem;
  color: #94a3b8;
}

.covoit-date-text {
  flex: 1;
  text-align: center;
}

.covoit-card__avatar--clickable {
  cursor: pointer;
  position: relative;
}

.covoit-card__avatar--clickable::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 0.5rem;
  background: rgba(0, 0, 0, 0);
  transition: background 0.15s;
  pointer-events: none;
}

.covoit-card__avatar--clickable:hover::after {
  background: rgba(0, 0, 0, 0.12);
}

.driver-avatar {
  width: 100%;
  height: 100%;
  border-radius: 0.5rem;
  background-color: var(--color-guc-darkblue, #1a3a5c);
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.5px;
}

/* Body */
.covoit-card__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.covoit-card__title {
  font-weight: 600;
  font-size: 0.95rem;
  color: #1f2a44;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-bottom: 0.15rem;
}

.covoit-badge {
  font-size: 0.7rem;
  font-weight: 600;
  border-radius: 999px;
  padding: 0.1rem 0.55rem;
  display: inline-flex;
  align-items: center;
}

.covoit-badge--driver {
  background: #e8f0fe;
  color: #2563eb;
}

.covoit-badge--passenger {
  background: #d1fae5;
  color: #065f46;
}


.covoit-card__meta {
  font-size: 0.82rem;
  color: #4a5568;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  margin-bottom: 0.2rem;
}

.covoit-card__driver-info {
  font-size: 0.82rem;
  color: #4a5568;
  margin-bottom: 0.15rem;
}

.covoit-card__info {
  font-size: 0.8rem;
  color: #6b7280;
  font-style: italic;
  margin-bottom: 0.15rem;
}

.covoit-card__actions {
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
  margin-top: auto;
}

.covoit-action-btn {
  border-radius: 999px !important;
  font-size: 0.8rem;
  padding: 0.25rem 0.75rem;
}

/* Stats */
.covoit-card__stats {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  align-items: center;
  min-width: 56px;
}

.covoit-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 0.4rem;
  padding: 0.3rem 0.5rem;
  width: 100%;
  text-align: center;
}

.covoit-stat--available {
  background: #d1fae5;
  color: #065f46;
}

.covoit-stat--full {
  background: #f1f5f9;
  color: #64748b;
}

.covoit-stat--neutral {
  background: #f1f5f9;
  color: #475569;
}

.covoit-stat__icon {
  font-size: 0.85rem;
  margin-bottom: 0.05rem;
}

.covoit-stat__value {
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.1;
}

.covoit-stat__label {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  opacity: 0.8;
}

.covoit-card__stats-mobile {
  display: none;
}

.covoit-stat-mini {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.covoit-stat-mini--available {
  background: #d1fae5;
  color: #065f46;
}

.covoit-stat-mini--full {
  background: #f1f5f9;
  color: #64748b;
}

.covoit-stat-mini--neutral {
  background: #f1f5f9;
  color: #475569;
}

/* Modal détails */
.carpool-details .details-head h5 {
  font-weight: 600;
  color: #172b4d;
}

.carpool-details .details-card {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 1rem;
  background: #fff;
}

.carpool-details .details-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem 1.25rem;
}

.carpool-details .details-label {
  font-size: 0.8rem;
  color: #6c757d;
}

.carpool-details .details-value {
  font-weight: 600;
  color: #172b4d;
}

.carpool-details .contact-name {
  font-weight: 600;
  color: #172b4d;
}

.carpool-details .contact-meta {
  font-size: 0.9rem;
  color: #6c757d;
}

@media (max-width: 576px) {
  .carpool-details .details-grid {
    grid-template-columns: 1fr;
  }

  .covoit-card__stats {
    display: none;
  }

  .covoit-card__stats-mobile {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin-top: 0.4rem;
  }

  .covoit-card__left {
    width: 68px;
  }

  .covoit-card__avatar {
    width: 68px;
    min-height: 68px;
  }

  .covoit-card {
    gap: 0.6rem;
    padding: 0.65rem 0.75rem;
  }
}
</style>
